import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/", end: true },
  { label: "电影分类", to: "/category" },
  { label: "影评资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-primary-900 text-background-100">
        <div className="flex h-8 items-center justify-between px-4 text-[11px] tracking-[0.14em] md:px-8">
          <span className="font-label uppercase">1980 — 1989 电影档案计划</span>
          <span className="hidden text-background-200/80 sm:block">
            本站为前端演示：搜索、筛选与收藏均在浏览器本地完成
          </span>
        </div>
      </div>

      <div className="border-b border-background-300/70 bg-background-50/95 backdrop-blur-md">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="80s电影 首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
              <i className="ri-film-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-semibold tracking-tight text-foreground-950">
                80s电影
              </span>
              <span className="mt-1 font-label text-[10px] uppercase tracking-[0.28em] text-secondary-600">
                Cinema Archive
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "text-primary-700 font-medium"
                      : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-accent-500 transition-opacity ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    ></span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100/80 px-3 py-1.5 lg:flex"
            >
              <span className="flex h-5 w-5 items-center justify-center text-secondary-500">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片或影评"
                aria-label="搜索影片或影评"
                className="w-36 bg-transparent text-sm text-foreground-900 placeholder:text-secondary-500 focus:outline-none"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-full bg-primary-600 px-3 py-1 font-label text-xs tracking-wider text-background-50 transition-colors hover:bg-primary-700"
              >
                搜索
              </button>
            </form>

            <Link
              to="/search"
              aria-label="搜索"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 lg:hidden"
            >
              <i className="ri-search-line"></i>
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 md:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-3-line text-lg"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
            <form onSubmit={submitSearch} className="mb-4 flex items-center gap-2 rounded-lg border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-secondary-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片或影评"
                aria-label="搜索影片或影评"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-secondary-500 focus:outline-none"
              />
              <button type="submit" className="whitespace-nowrap font-label text-sm tracking-wider text-primary-700">
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-background-200 py-3 text-base ${
                      isActive ? "text-primary-700" : "text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-secondary-500"></i>
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}